// Styles
import "./App.scss";

// Hooks & modules
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import {
	useGetQuotationInfoQuery,
	useGetQuotationViewQuery,
	useAuthsOtlQuery
} from "./pages/recap/services/recap.service";
import { useAuthsOtpQuery } from "./pages/recap/services/token-manager-service.service";
import {
	selectIsQuotationFreezed,
	setCurrentId,
	setQuotationFreezeStatus
} from "./store/recap/recapSlice";
import { selectCurrentStep, setSteps } from "./store/metro/metroSlice";
import { setError } from "./store/error/errorSlice";
import { Routes, Route, useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import useScript from "./hooks/useScript";

// Components
import Footer from "./components/footer/Footer";
import ThankYouPage from "./pages/typ/Typ";
import RecapPage from "./pages/recap/Recap";
import CGAPage from "./pages/CGA/CGA";
import DonePage from "./pages/done/done";
import UnauthorizedPage from "./pages/unauthorized/Unauthorized";
import ErrorPage from "./pages/error/Error";
import Drawer from "./components/drawer/Drawer";
import Header from "./components/header/Header";
import Loader from "./components/loader/Loader";

// Utils
import ScrollToTop from "app/components/_shared/atoms/ScrollToTop/ScrollToTop";
import Metro, { Step } from "./components/metro/Metro";
import { steps } from "../utils/core/constants";
import { metroVariants } from "../utils/core/motionConfig";

// Store
import { setDrawerContent } from "./store/drawer/drawer";
import {
	setMandatoryConsentsState,
	setOptionalConsentsState
} from "./store/privacyConsents/privacyConsentsSlice";

// Types
import {
	GetQuotationInfoQuery,
	GetQuotationViewQuery,
	PostAuthsOtpQuery,
	AuthsOTPBody,
	AuthsOTL,
	AuthsOTP,
	ErrorOtp
} from "app/pages/recap/recap.types";

import { globalConfig } from "configuration";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";

const App = (): JSX.Element => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [searchParams] = useSearchParams();
	const config = globalConfig.get();
	useScript(config.REACT_APP_ADOBE_LAUNCH_URL, true);
	const id =
		searchParams.get("qid") || /* istanbul ignore next: this is a fallback always true */ "";
	const authCode =
		searchParams.get("authCode") ||
		/* istanbul ignore next: this is a fallback always true */ "";
	const currentStep = useAppSelector<Step>(selectCurrentStep);
	const isFreezed = useAppSelector<boolean>(selectIsQuotationFreezed);

	useEffect(() => {
		/* istanbul ignore else */
		if (window.location.pathname !== steps[0].pageName) {
			navigate(`${steps[0].pageName}?${searchParams}`);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const {
		data: dataInfo,
		error: errorInfo,
		isLoading: isLoadingInfo,
		isFetching: isFetchingInfo
	}: GetQuotationInfoQuery = useGetQuotationInfoQuery<GetQuotationInfoQuery>(id, {
		skip: !id || !config
	});
	const isInfoReady = !isLoadingInfo && !isFetchingInfo && !!dataInfo;

	const [slt, setSlt] = useState<string>(() => sessionStorage.getItem("slt") ?? "");
	const [rtk, setRtk] = useState<string | null>(() => sessionStorage.getItem("rtk"));
	const status = dataInfo?.status;
	const sourceId = dataInfo?.sourceSystem;
	const baseSkip =
		!status || !["AWAITING_SIGNATURE", "FREEZED"].includes(status) || !!(slt && rtk) || !config;

	const isOtp = dataInfo?.authenticationType === "OTP";
	const isOtl = dataInfo?.authenticationType === "OTL";

	const authType = dataInfo?.authenticationType;
	const authTypeReady = !!authType;
	const shouldRunAuth = !!id && isInfoReady && authTypeReady && !baseSkip;

	const skipOtp = !shouldRunAuth || !authCode || !isOtp;
	const skipOtl = !shouldRunAuth || !isOtl;

	const body = { authCode, id, sourceId } as AuthsOTPBody;

	const { data: dataAuthsOtp, error: errorAuthsOtp }: PostAuthsOtpQuery =
		useAuthsOtpQuery<PostAuthsOtpQuery>(body, {
			skip: skipOtp
		});

	const { data: dataAuthsOtl, error: errorAuthsOtl } = useAuthsOtlQuery(body, {
		skip: skipOtl
	});

	let dataAuths: AuthsOTL | AuthsOTP | undefined,
		errorAuths: ErrorOtp | FetchBaseQueryError | SerializedError | undefined;

	if (!!dataInfo?.authenticationType && dataInfo?.authenticationType === "OTP") {
		dataAuths = dataAuthsOtp;
		errorAuths = errorAuthsOtp;
	} else if (!!dataInfo?.authenticationType && dataInfo?.authenticationType === "OTL") {
		dataAuths = dataAuthsOtl;
		errorAuths = errorAuthsOtl;
	}

	const effectiveSlt = dataAuths?.slt ?? slt;
	const hasValidSession = !!slt && !!rtk;
	const isAuthSuccess = !!dataAuths?.slt && !!dataAuths?.rtk;

	/* istanbul ignore next */
	const handleView = !config || (!!authCode && !slt) || !id || !dataInfo || !!errorInfo;
	/* istanbul ignore next */
	const shouldFetchViewOTL =
		!!config && !!id && !!dataInfo && !errorInfo && (hasValidSession || isAuthSuccess);

	const skipView = isOtp ? handleView : !shouldFetchViewOTL;
	const {
		data: dataView,
		isLoading: isLoadingView,
		error: errorView,
		isFetching: isFetchingView
	}: GetQuotationViewQuery = useGetQuotationViewQuery<GetQuotationViewQuery>(
		{ quotationId: id, bearer: effectiveSlt },
		{ skip: skipView }
	);

	useEffect(() => {
		/* istanbul ignore else */
		if (dataView?.isQuotationFreezed !== undefined) {
			dispatch(setQuotationFreezeStatus(dataView.isQuotationFreezed));
		}

		/* istanbul ignore else */
		if (dataView?.offerSection?.drawer) {
			dispatch(setDrawerContent(dataView.offerSection?.drawer));
		}

		/* istanbul ignore else */
		if (dataView?.privacySection?.mandatoryConsentItems) {
			const formattedMandatoryConsents = [
				...dataView.privacySection.mandatoryConsentItems
			].map((item) => ({ ...item, given: false }));
			dispatch(setMandatoryConsentsState(formattedMandatoryConsents));
		}
		/* istanbul ignore else */
		if (dataView?.privacySection?.optionalConsentItems) {
			dispatch(setOptionalConsentsState(dataView?.privacySection?.optionalConsentItems));
		}

		/* istanbul ignore else */
		if (dataView) {
			const { privacySection, generalConsentsSections } = dataView;
			const availableSteps = [...steps];
			/* istanbul ignore else */
			if (
				(!privacySection || Object.keys(privacySection).length === 0) &&
				!generalConsentsSections?.length
			) {
				availableSteps.splice(1, 1);
			}
			const signatureSteps = availableSteps.map(({ pageName }, i) => ({
				pageName,
				index: i + 1
			}));
			dispatch(setSteps(signatureSteps));
		}
	}, [dataView, dispatch]);

	useEffect(() => {
		if (dataAuths?.slt && dataAuths?.rtk) {
			sessionStorage.setItem("slt", dataAuths.slt);
			sessionStorage.setItem("rtk", dataAuths.rtk);
			setSlt(dataAuths.slt);
			setRtk(dataAuths.rtk);
		}
	}, [dataAuths]);

	useEffect(() => {
		/* istanbul ignore else */
		if (id) {
			dispatch(setCurrentId(id));
		}
		/* istanbul ignore else */
		if ((errorView && errorView.status === 401 && dataInfo && id) || errorAuths) {
			navigate("/unauthorized", { state: { currentUrl: window.location.href } });
		}
		/* istanbul ignore else */
		if (
			((errorView && errorView.status !== 401) || errorInfo || errorAuths) &&
			(!isLoadingView || !isLoadingInfo)
		) {
			dispatch(setError(errorView || errorInfo || errorAuths));
			navigate("/error", { state: { currentUrl: window.location.href } });
		}
	}, [
		errorView,
		errorInfo,
		errorAuths,
		isLoadingInfo,
		isLoadingView,
		navigate,
		dataInfo,
		dispatch,
		id
	]);

	const motionClass = "metro-motion";
	return (
		<div className={`App ${isFreezed ? "freezed" : ""}`}>
			<ScrollToTop />
			<Header
				expiryWarning={
					window.location.pathname !== "/done" && dataView?.heading?.expiryWarning
				}
			/>
			<Drawer />
			{isLoadingInfo || isLoadingView || isFetchingInfo || isFetchingView ? (
				<div className="loader-container">
					<Loader />
				</div>
			) : (
				<main>
					<motion.div
						className={motionClass}
						animate="visible"
						variants={metroVariants}
						custom={{ currentPath: window.location.pathname }}
						data-testid="metro-motion"
					>
						<Metro currentStep={currentStep} />
					</motion.div>
					<Routes>
						<Route path="/sign" element={<RecapPage dataView={dataView} />} />
						<Route path="/sign-2" element={<CGAPage dataView={dataView} />} />
						<Route
							path="/sign-3"
							element={
								<DonePage
									dataView={dataView}
									signatureMode={
										dataInfo?.signatureMode ? dataInfo.signatureMode : ""
									}
								/>
							}
						/>
						<Route path="/unauthorized" element={<UnauthorizedPage />} />
						<Route path="/error" element={<ErrorPage />} />
						<Route path="/done" element={<ThankYouPage />} />
					</Routes>
				</main>
			)}
			{isFreezed && <Footer text={dataView?.footer?.text} link={dataView?.footer?.link} />}
		</div>
	);
};

export default App;
